import * as React from 'react';
import Box from '@mui/material/Box';
import { Controller, useFormContext, useFormState } from 'react-hook-form';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { Close } from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';

type ControlledAutocompleteDropdownMultipleProps<T> = {
  nameProp: string;
  keyProp?: string; // Jämför valda värden och alternativ på den här egenskapen istället för på objektreferens
  label: string;
  name: string;
  fetchMethod: () => Promise<T[]>;
  callback: (selected: any[] | undefined) => void;
  componentWidth?: string;
  limitTags?: number;
  disabled?: boolean;
};

export const ControlledAutocompleteDropdownMultiple = <T,>(props: ControlledAutocompleteDropdownMultipleProps<T>) => {
  const { nameProp, keyProp, label, name, fetchMethod, callback, componentWidth = '15rem', limitTags = 2, disabled = false } = props;
  const [selectedValue, setSelectedValue] = React.useState<any[] | undefined>();
  const [inputValue, setInputValue] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState<readonly T[]>([]);
  const loading = open && options.length === 0;

  const isMounted = useIsMounted();

  const { errors } = useFormState();
  const { setValue, getValues } = useFormContext();

  React.useEffect(() => {
    let active = true;

    if (!loading) {
      return undefined;
    }

    if (!options.length) {
      (async () => {
        const result = await fetchMethod();

        if (active && isMounted()) {
          setOptions([...result]);
        }
      })();
    }

    return () => {
      active = false;
    };
  }, [fetchMethod, isMounted, loading, options.length]);

  // React.useEffect(() => {
  //   if (!open && isMounted()) {
  //     setOptions([]);
  //   }
  // }, [open, isMounted]);

  const componentId = `autocomplete-${Date.now().toString(36) + Math.random().toString(36).substring(2)}`;

  let isError = false;
  let errorMessage = '';

  if (errors && Object.prototype.hasOwnProperty.call(errors, name)) {
    isError = true;
    errorMessage = errors[name]?.message?.toString() || '';
  }

  React.useEffect(() => {
    setSelectedValue(getValues(name));
  }, [getValues, name]);

  return (
    <Autocomplete
      multiple
      limitTags={limitTags}
      id={componentId}
      disabled={disabled}
      options={options}
      autoHighlight
      open={open}
      openOnFocus
      disableCloseOnSelect
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
      }}
      loading={loading}
      getOptionLabel={(option) => option[nameProp]}
      isOptionEqualToValue={(option, val) => (keyProp ? option[keyProp] === val[keyProp] : option === val)}
      onChange={(event: any, newValue: any) => {
        setValue(name, newValue);
        setSelectedValue(newValue);
        callback(newValue);
      }}
      value={selectedValue || []}
      inputValue={inputValue}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          size='small'
          sx={{ width: componentWidth }}
          label={label}
          error={isError}
          InputLabelProps={{ shrink: true }}
          inputProps={{
            ...params.inputProps,
            autoComplete: 'new-password', // disable autocomplete and autofill
          }}
          helperText={errorMessage ? <em>{errorMessage}</em> : null}
        />
      )}
      clearIcon={<Close fontSize='small' />}
      sx={{
        '& .MuiChip-sizeMedium ': { height: 'auto', borderRadius: 1 },
        '& .MuiChip-deleteIcon ': { fontSize: '18px' },
      }}
    />
  );
};
